package com.sddamico.draftmachine.ds.core

import androidx.compose.runtime.Composable
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.backgroundImageRaw
import com.sddamico.draftmachine.ds.boxShadowRaw
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.displayInlineBlock
import com.sddamico.draftmachine.ds.dmStamp
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.AlignSelf
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span

/**
 * A hairline steel separator — horizontal rule, optional stamped label or riveted stud, or a
 * vertical divider. Ports `components/core/Divider.jsx`.
 *
 * @param label a stamped mono micro-label sits centered between two rule segments.
 * @param riveted a corner-style stud sits centered (only when there is no [label]).
 * @param vertical renders a 1px vertical rule that stretches to its parent's height.
 */
@Composable
fun Divider(
  label: String? = null,
  modifier: Modifier = Modifier,
  riveted: Boolean = false,
  vertical: Boolean = false,
) {
  if (vertical) {
    Span(
      attrs = Modifier
        .displayInlineBlock()
        .width(1.px)
        .alignSelf(AlignSelf.Stretch)
        .backgroundImageRaw("linear-gradient(180deg, transparent, #4B5A68, transparent)")
        .then(modifier)
        .toAttrs { attr("aria-hidden", "true") }
    ) {}
    return
  }

  val line = Modifier
    .flex("1")
    .height(1.px)
    .backgroundImageRaw("linear-gradient(90deg, transparent, #4B5A68 20%, #4B5A68 80%, transparent)")

  Div(
    attrs = Modifier
      .displayFlex()
      .alignItems(AlignItems.Center)
      .gap(DmSpacing.space3)
      .fillMaxWidth()
      .then(modifier)
      .toAttrs { attr("role", "separator") }
  ) {
    Span(attrs = line.toAttrs { attr("aria-hidden", "true") }) {}
    if (label != null) {
      Span(attrs = Modifier.dmStamp().flex("0 0 auto").toAttrs()) { SpanText(label) }
    }
    if (riveted && label == null) {
      Span(
        attrs = Modifier
          .width(7.px)
          .height(7.px)
          .borderRadius(50.percent)
          .backgroundImageRaw("radial-gradient(circle at 35% 30%, #B0BFCC, #4B5A68)")
          .boxShadowRaw("inset 0 -1px 1px rgba(0,0,0,.5)")
          .toAttrs { attr("aria-hidden", "true") }
      ) {}
    }
    if (label != null || riveted) {
      Span(attrs = line.toAttrs { attr("aria-hidden", "true") }) {}
    }
  }
}
